import { useMemo } from "react";
import { useCashAvailable } from "hooks/firestore/user/useAccountBalances";
import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import { selectCombinationsForLegCount } from "@/features/betslip/stores/computed";

export type PromoScope =
  | { type: "single"; outcomeId: string }
  | { type: "multi" }
  | { type: "combination"; comboType: string };

/**
 * Promo balance left after accounting for promo-cash flags on the slip.
 * Replaces the former `state.remainingPromoBalance` computed field —
 * balance is server state, so the derivation lives here (React hook)
 * rather than in zustand-computed.
 *
 * Pass a scope to exclude that entry's own promo stake, giving the promo
 * cash available *to it* (the remainder plus what it already holds). No
 * scope returns the slip-wide remainder.
 */
export const useRemainingPromoBalance = (exclude?: PromoScope): number => {
  const { promo: promoBalance } = useCashAvailable();
  const stakes = useBetslipStore((state) => state.stakes);
  const promoCash = useBetslipStore((state) => state.promoCash);
  // A combo size the current leg count can't form is never submitted, so it
  // reserves no promo cash.
  const combinations = useBetslipStore(selectCombinationsForLegCount);

  const excludeType = exclude?.type;
  const excludeKey =
    exclude?.type === "single"
      ? exclude.outcomeId
      : exclude?.type === "combination"
        ? exclude.comboType
        : null;

  return useMemo(() => {
    let used = 0;
    for (const [outcomeId, isUsing] of Object.entries(promoCash.singles)) {
      if (!isUsing) continue;
      if (excludeType === "single" && outcomeId === excludeKey) continue;
      // An inactive selection keeps its applied promo reserved until the user
      // de-selects it — it isn't silently returned to the pool for others.
      used += stakes.singles[outcomeId] ?? 0;
    }
    if (promoCash.multi && excludeType !== "multi") used += stakes.multi;
    for (const [comboType, combo] of Object.entries(combinations)) {
      if (!combo.isPromo) continue;
      if (excludeType === "combination" && comboType === excludeKey) continue;
      used += combo.total;
    }
    return promoBalance - used;
  }, [
    promoBalance,
    stakes.singles,
    stakes.multi,
    combinations,
    promoCash,
    excludeType,
    excludeKey,
  ]);
};
